// AGC: PricingCards - Visual pricing cards for tier comparison
// AGC: Shows pricing and key features for FREE, PRO, PREMIUM tiers

import React from 'react'
import { 
  Gift, 
  Star, 
  Crown, 
  Check, 
  X,
  Zap,
  ArrowRight,
} from 'lucide-react'
import { useSubscription } from '../../context/SubscriptionContext'
import { useTranslation } from '../../hooks/useTranslation'
import {
  type SubscriptionTier,
  TIER_DISPLAY_INFO,
} from '../../types/subscription'

interface PricingCardsProps {
  onSelectTier?: (tier: SubscriptionTier) => void
  showCurrent?: boolean
  compact?: boolean
}

/**
 * AGC: Icon component for tiers
 */
const TierIcon: React.FC<{ tier: SubscriptionTier; className?: string }> = ({ tier, className = 'w-8 h-8' }) => {
  switch (tier) {
    case 'free':
      return <Gift className={className} />
    case 'pro':
      return <Star className={className} />
    case 'premium':
      return <Crown className={className} />
  }
}

/**
 * AGC: Feature list item
 */
const FeatureItem: React.FC<{
  included: boolean
  text: string
  highlight?: boolean
}> = ({ included, text, highlight }) => (
  <li className={`flex items-start gap-2 ${highlight ? 'font-medium' : ''}`}>
    {included ? (
      <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
    ) : (
      <X className="w-4 h-4 text-gray-300 dark:text-slate-600 flex-shrink-0 mt-0.5" />
    )}
    <span className={included ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 dark:text-slate-500'}>
      {text}
    </span>
  </li>
)

/**
 * AGC: Single pricing card component
 */
const PricingCard: React.FC<{
  tier: SubscriptionTier
  isCurrentTier: boolean
  onSelect?: () => void
  compact?: boolean
}> = ({ tier, isCurrentTier, onSelect, compact }) => {
  const { t } = useTranslation()
  const { tierConfigs } = useSubscription()
  const config = tierConfigs[tier]
  const pricing = { monthly: config?.price?.monthly ?? 0, annual: config?.price?.annual ?? 0 }
  const display = TIER_DISPLAY_INFO[tier]
  const limits = config?.limits

  // AGC: Key features for each tier - using translations
  const getFeatures = (): { text: string; included: boolean; highlight?: boolean }[] => {
    switch (tier) {
      case 'free':
        return [
          { text: `${limits.maxHectares} ${t('subscription.features.hectares')}`, included: true },
          { text: `${limits.maxFields} ${t('subscription.features.fields')}`, included: true },
          { text: `${limits.maxTasksPerMonth} ${t('subscription.features.tasksPerMonth')}`, included: true },
          { text: `${limits.maxVoiceCommandsPerMonth} ${t('subscription.features.voiceCommands')}`, included: true },
          { text: t('subscription.features.offlineVoice'), included: false },
          { text: t('subscription.features.whatsApp'), included: false },
          { text: t('subscription.features.humanAdvisor'), included: false },
        ]
      case 'pro':
        return [
          { text: `${limits.maxHectares} ${t('subscription.features.hectares')}`, included: true, highlight: true },
          { text: `${limits.maxFields} ${t('subscription.features.fields')}`, included: true },
          { text: `${limits.maxTasksPerMonth} ${t('subscription.features.tasksPerMonth')}`, included: true },
          { text: `${limits.maxVoiceCommandsPerMonth} ${t('subscription.features.voiceCommands')}`, included: true },
          { text: t('subscription.features.offlineVoice'), included: true, highlight: true },
          { text: `${limits.maxWhatsAppAudioPerMonth} ${t('subscription.features.whatsAppAudios')}`, included: true, highlight: true },
          { text: t('subscription.features.humanAdvisor'), included: false },
        ]
      case 'premium':
        return [
          { text: t('subscription.features.unlimitedHectares'), included: true, highlight: true },
          { text: t('subscription.features.unlimitedFields'), included: true },
          { text: t('subscription.features.unlimitedTasks'), included: true },
          { text: t('subscription.features.unlimitedVoiceCommands'), included: true },
          { text: t('subscription.features.offlineVoice'), included: true },
          { text: t('subscription.features.unlimitedWhatsApp'), included: true },
          { text: t('subscription.features.humanAdvisor'), included: true, highlight: true },
        ]
    }
  }

  const features = getFeatures()

  if (compact) {
    return (
      <div className={`
        p-4 rounded-xl border-2 transition-all
        ${isCurrentTier 
          ? `${display.borderColor} ${display.bgColor}` 
          : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600'
        }
        ${display.recommended && !isCurrentTier ? 'ring-2 ring-blue-500 ring-offset-2 dark:ring-offset-slate-900' : ''}
      `}>
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${display.bgColor}`}>
            <TierIcon tier={tier} className={`w-5 h-5 ${display.color}`} />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">
              {display.name}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {pricing.monthly === 0 ? t('subscription.pricing.free') : `R$ ${pricing.monthly}/${t('subscription.pricing.perMonth')}`}
            </p>
          </div>
          {isCurrentTier ? (
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${display.bgColor} ${display.color}`}>
              {t('subscription.pricing.current')}
            </span>
          ) : onSelect && (
            <button
              onClick={onSelect}
              className="px-3 py-1.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {t('subscription.pricing.select')}
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={`
      relative flex flex-col rounded-2xl border-2 transition-all overflow-hidden
      ${isCurrentTier 
        ? `${display.borderColor} shadow-lg` 
        : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600'
      }
      ${display.recommended && !isCurrentTier ? 'ring-2 ring-blue-500 ring-offset-2 dark:ring-offset-slate-900' : ''}
    `}>
      {/* AGC: Recommended badge */}
      {display.recommended && (
        <div className="absolute top-0 right-0 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
          Recomendado
        </div>
      )}
      
      {/* AGC: Current plan badge */}
      {isCurrentTier && (
        <div className={`absolute top-0 left-0 ${display.bgColor} ${display.color} text-xs font-bold px-3 py-1 rounded-br-lg`}>
          Plano Atual
        </div>
      )}

      {/* AGC: Header */}
      <div className={`p-6 ${display.bgColor}`}>
        <div className="flex items-center gap-3 mb-4">
          <div className={`p-3 rounded-xl bg-white/50 dark:bg-slate-800/50`}>
            <TierIcon tier={tier} className={`w-8 h-8 ${display.color}`} />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {display.name}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {display.description}
            </p>
          </div>
        </div>
        
        {/* AGC: Price */}
        <div className="flex items-baseline gap-1">
          {pricing.monthly === 0 ? (
            <span className="text-4xl font-bold text-gray-900 dark:text-gray-100">Grátis</span>
          ) : (
            <>
              <span className="text-lg text-gray-500 dark:text-gray-400">R$</span>
              <span className="text-4xl font-bold text-gray-900 dark:text-gray-100">{pricing.monthly}</span>
              <span className="text-gray-500 dark:text-gray-400">/mês*</span>
            </>
          )}
        </div>
        
        {pricing.annual > 0 && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            {t('subscription.pricing.or')} R$ {pricing.annual}/{t('subscription.pricing.perYear')} ({t('subscription.pricing.save')} R$ {pricing.annualSavings})
          </p>
        )}
      </div>

      {/* AGC: Features */}
      <div className="flex-1 p-6 bg-white dark:bg-slate-800">
        <ul className="space-y-3 text-sm">
          {features.map((feature, index) => (
            <FeatureItem 
              key={index} 
              included={feature.included} 
              text={feature.text}
              highlight={feature.highlight}
            />
          ))}
        </ul>
      </div>

      {/* AGC: CTA Button */}
      <div className="p-6 pt-0 bg-white dark:bg-slate-800">
        {isCurrentTier ? (
          <div className={`
            w-full py-3 px-4 rounded-xl text-center font-semibold
            ${display.bgColor} ${display.color}
          `}>
            <Check className="w-5 h-5 inline-block mr-2" />
            {t('subscription.pricing.currentPlan')}
          </div>
        ) : onSelect && (
          <button
            onClick={onSelect}
            className={`
              w-full py-3 px-4 rounded-xl font-semibold transition-all
              flex items-center justify-center gap-2
              ${tier === 'premium' 
                ? 'bg-gradient-to-r from-yellow-500 to-amber-500 text-white hover:from-yellow-600 hover:to-amber-600 shadow-lg shadow-yellow-500/25' 
                : tier === 'pro'
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600'
              }
            `}
          >
            {tier === 'free' ? t('subscription.pricing.useFree') : (
              <>
                <Zap className="w-4 h-4" />
                {t('subscription.pricing.upgrade')}
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * AGC: Pricing Cards Component
 */
export const PricingCards: React.FC<PricingCardsProps> = ({
  onSelectTier,
  showCurrent = true,
  compact = false,
}) => {
  const { tier: currentTier, updateTier, isLoading } = useSubscription()
  const { t } = useTranslation()

  const handleSelectTier = async (tier: SubscriptionTier) => {
    if (onSelectTier) {
      onSelectTier(tier)
    } else {
      try {
        await updateTier(tier)
      } catch (error) {
        console.error('Error updating tier:', error)
      }
    }
  }

  const tiers: SubscriptionTier[] = ['free', 'pro', 'premium']

  if (compact) {
    return (
      <div className="space-y-3">
        {tiers.map(tier => (
          <PricingCard
            key={tier}
            tier={tier}
            isCurrentTier={showCurrent && currentTier === tier}
            onSelect={() => handleSelectTier(tier)}
            compact
          />
        ))}
      </div>
    )
  }

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {tiers.map(tier => (
          <PricingCard
            key={tier}
            tier={tier}
            isCurrentTier={showCurrent && currentTier === tier}
            onSelect={() => handleSelectTier(tier)}
          />
        ))}
      </div>
      {/* AGC: Pricing footnote — first year promo + link to full terms */}
      <p className="mt-4 text-xs text-gray-500 dark:text-gray-400 text-center leading-relaxed">
        {t('subscription.pricing.renewalNote') || '* Preços promocionais válidos para o primeiro ano. Consulte os termos completos.'}
        {' '}
        <a
          href="https://droidfarm-app.web.app/pricing-terms.html"
          target="_blank"
          rel="noopener noreferrer"
          className="text-emerald-600 dark:text-emerald-400 underline hover:text-emerald-700"
        >
          {t('subscription.pricing.termsLink') || 'Ver termos completos'}
        </a>
      </p>
    </div>
  )
}

export default PricingCards

